import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import en from "./locales/en.json";
import de from "./locales/de.json";
import pl from "./locales/pl.json";
import es from "./locales/es.json";
import it from "./locales/it.json";
import ro from "./locales/ro.json";
import fr from "./locales/fr.json";

// Persisted under this key so unauthenticated users keep their choice.
export const LANG_STORAGE_KEY = "safebook_lang";
export const DEFAULT_LANG = "en";

export const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "ro", label: "Română", flag: "🇷🇴" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
];

const DICTS = { en, de, pl, es, it, ro, fr };
const SUPPORTED = LANGUAGES.map((l) => l.code);

// First-visit only: map the browser's preferred languages to a supported locale.
function detectBrowserLang() {
  try {
    const prefs = [];
    if (navigator.languages && navigator.languages.length) prefs.push(...navigator.languages);
    if (navigator.language) prefs.push(navigator.language);
    for (const p of prefs) {
      const code = String(p).toLowerCase().split("-")[0];
      if (SUPPORTED.includes(code)) return code;
    }
  } catch (e) { /* ignore */ }
  return DEFAULT_LANG;
}

export function getStoredLang() {
  try {
    const s = localStorage.getItem(LANG_STORAGE_KEY);
    if (s && SUPPORTED.includes(s)) return s; // manual choice wins — never overridden
  } catch (e) { /* ignore */ }
  return detectBrowserLang(); // first-time visitor: fall back to browser language
}

function resolve(dict, key) {
  return String(key).split(".").reduce((o, k) => (o != null && o[k] != null ? o[k] : undefined), dict);
}

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(getStoredLang);

  useEffect(() => {
    try { localStorage.setItem(LANG_STORAGE_KEY, lang); } catch (e) { /* ignore */ }
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((code) => { if (SUPPORTED.includes(code)) setLangState(code); }, []);

  // Active language with a STRICT English fallback for any missing key.
  const t = useCallback((key, fallback) => {
    const v = resolve(DICTS[lang] || DICTS.en, key);
    if (v != null) return v;
    const base = resolve(DICTS.en, key);
    return base != null ? base : (fallback != null ? fallback : key);
  }, [lang]);

  return <I18nCtx.Provider value={{ lang, setLang, t, languages: LANGUAGES }}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nCtx);
  if (!ctx) return { lang: DEFAULT_LANG, setLang: () => {}, t: (k, f) => (f != null ? f : k), languages: LANGUAGES };
  return ctx;
}

export const useT = () => useI18n().t;
